import React from 'react';

import type { NextPage } from 'next';
import { useRouter } from 'next/router';

import { CheckIcon } from '@heroicons/react/outline';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  FadeIn,
  Footer,
  GradientBg,
  Header,
  InsuranceCarousel,
  InsuranceSection,
  PageHeader
} from '../components';
import { freelance } from '../constants/insuranceTypes';

const reasons = [
  {
    name: 'Mistakes',
    description: 'Work mistakes or oversights'
  },
  {
    name: 'Lawsuit',
    description: 'Reputational damage and client lawsuit'
  },
  {
    name: 'Property',
    description: 'Lost, stolen, or damaged property and equipment'
  },
  {
    name: 'Loss',
    description: 'Loss of income and disability'
  },
  {
    name: 'Contracts',
    description: 'Client contract requirements'
  },
  {
    name: 'Marketplace Requirements',
    description: 'Insurance requirements by marketplaces (i.e., Fiverr)'
  }
];

const types = Object.keys(freelance).map((type: string) => ({
  title: type,
  icon: freelance[type]
}));

const Freelancer: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="Freelancer"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: 'Freelancer'
        }}
      />
      <div className="overflow-hidden">
        <GradientBg />
        <Header />

        <PageHeader
          title="Freelancer"
          description="Running your own business comes with plenty of achievements, but every business also has its own risks."
          image={
            <img
              src="/img/freelancer.svg"
              className="mx-auto max-w-2xl w-full mt-8 md:mt-0"
              aria-hidden="true"
              alt="Illustration of man at desk smiling"
            />
          }
        />

        {/* Section-1 */}
        <div className="max-w-7xl mx-auto my-20 lg:my-32 px-4 sm:px-6 lg:px-8 lg:grid lg:grid-cols-5 lg:gap-x-8">
          <div className="lg:col-span-2 text-center lg:text-left">
            <FadeIn>
              <p className="text-base font-semibold text-primary uppercase tracking-wide">
                Everything you need
              </p>
              <h2 className="mt-2 text-5xl font-extrabold text-alt">
                Why do <span className="text-primary">freelancers</span> need
                insurance?
              </h2>
            </FadeIn>

            <p className="mt-4 text-lg text-gray-500">
              As a freelancer, you don&lsquo;t have the benefit of working under
              the umbrella of an employer&lsquo;s insurance coverage. No matter
              what type of freelance work you&lsquo;re doing, every profession
              comes with a particular set of common risks. Obtaining the right
              insurance is crucial to protect your business from:
            </p>
          </div>
          <div className="mt-12 lg:mt-0 lg:col-span-3">
            <dl className="space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:grid-rows-3 sm:grid-flow-col sm:gap-x-6 sm:gap-y-10 lg:gap-x-8">
              {reasons.map(reason => (
                <div key={reason.name} className="relative">
                  <dt>
                    <CheckIcon
                      className="absolute h-6 w-6 text-green-500"
                      aria-hidden="true"
                    />
                    <p className="ml-9 text-lg leading-6 font-medium text-gray-900">
                      {reason.name}
                    </p>
                  </dt>
                  <dd className="mt-2 ml-9 text-base text-gray-500">
                    {reason.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* Section-2 */}
        <InsuranceSection
          bgColor="bg-white"
          title={
            <FadeIn>
              <h2 className=" mt-2 text-5xl font-extrabold text-alt">
                What does <span className="text-primary">freelancers</span>{' '}
                insurance cover?{' '}
              </h2>
            </FadeIn>
          }
          description={
            <div className="max-w-2xl mx-auto text-xl text-gray-500">
              <FadeIn>
                <p className="mt-6">
                  Choosing the right freelance business insurance can help
                  protect you from costly risks and liabilities, but the right
                  policies for your business will depend on the services you
                  offer.
                </p>
                <p className="mt-4">
                  Here are some of the most popular insurance policies for small
                  business owners, covering the most common risks you&apos;ll
                  face as a freelancer:
                </p>
              </FadeIn>
            </div>
          }
        />

        {/* Section-3 */}
        <div className="mx-auto max-w-3xl lg:max-w-7xl px-4 sm:px-6 lg:px-8 py-8 lg:py-20 text-center">
          <FadeIn>
            <h2 className="mt-2 text-5xl font-extrabold  text-alt">
              What types of <span className="text-primary">freelancers</span> do
              we cover?
            </h2>
          </FadeIn>
          <div className="mt-12 px-2 md:px-12 sm:px-0">
            <InsuranceCarousel items={types} />
          </div>
        </div>

        {/* Section-4 */}
        <div className="mx-auto max-w-3xl lg:max-w-7xl py-8 lg:py-20 px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <FadeIn>
                <h2 className="mt-2 text-5xl font-extrabold  text-alt text-center lg:text-left">
                  How much does{' '}
                  <span className="text-primary">freelancer insurance</span>{' '}
                  cost?
                </h2>
              </FadeIn>
              <div className="mt-4 text-lg text-gray-500 text-center sm:text-left">
                <p>
                  Coverdash helps you identify the appropriate coverage to suit
                  your freelancer needs without breaking the bank. The exact
                  amount you pay will depend on:
                </p>

                <ul className="list-disc p-5 text-left">
                  <li>The types of freelance projects you work on</li>
                  <li>Where you and your clients are located</li>
                  <li>Your revenue and employee payroll</li>
                  <li>Your level of industry experience</li>
                </ul>
              </div>
            </div>

            <div className="justify-right items-right">
              <img
                src="/img/insurance-cost.jpg"
                className="h-full w-full lg:w-[140%]"
                alt="illustration of woman pointing at infographic"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
        <BottomGraphic />
        <Footer />
      </div>
    </>
  );
};

export default Freelancer;
